/* ============================================================
   Atelier Diamante — Diamond Finder
   React 18 Web Component island.

   Data sources (in priority order):
   1. Storefront API (GraphQL) — full metafields, all 6,888 stones, paginated
      Requires window.adTheme.storefrontToken to be set in theme.liquid
   2. /collections/{handle}/products.json — tags-based fallback, 250 per page

   Filters: shape, carat range, color (D-K), clarity, cut, cert
   Display: 24 cards per page, load more, compare tray integration
   ============================================================ */

/* ── Storefront API query ──────────────────────────────────────────────────── */
const GQL = `
query FinderPage($handle: String!, $first: Int!, $cursor: String) {
  collection(handle: $handle) {
    products(first: $first, after: $cursor) {
      pageInfo { hasNextPage endCursor }
      edges {
        node {
          id handle title
          priceRange { minVariantPrice { amount currencyCode } }
          featuredImage { url(transform:{maxWidth:500}) altText }
          shape:    metafield(namespace:"custom", key:"shape")         { value }
          carat:    metafield(namespace:"custom", key:"carat")         { value }
          color:    metafield(namespace:"custom", key:"color")         { value }
          clarity:  metafield(namespace:"custom", key:"clarity")       { value }
          cut:      metafield(namespace:"custom", key:"cut")           { value }
          cert:     metafield(namespace:"custom", key:"cert_authority"){ value }
          origin:   metafield(namespace:"custom", key:"origin")        { value }
        }
      }
    }
  }
}`;

/* ── Normalise a Storefront node → flat finder record ──────────────────────── */
function norm(node) {
  return {
    id:      node.id,
    handle:  node.handle,
    title:   node.title,
    image:   node.featuredImage?.url || '',
    price:   parseFloat(node.priceRange?.minVariantPrice?.amount || 0),
    currency: node.priceRange?.minVariantPrice?.currencyCode || 'USD',
    shape:   node.shape?.value    || '',
    carat:   parseFloat(node.carat?.value    || 0),
    color:   node.color?.value    || '',
    clarity: node.clarity?.value  || '',
    cut:     node.cut?.value      || '',
    cert:    node.cert?.value     || '',
    origin:  node.origin?.value   || '',
  };
}

/* ── Fetch all diamonds from Storefront API (cursor-paginated) ─────────────── */
async function fetchStorefront(shop, token, handle) {
  const url     = `https://${shop}/api/2024-10/graphql.json`;
  const headers = { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token };
  let all = [], cursor = null, hasMore = true;
  while (hasMore) {
    const vars = { handle: handle || 'all-diamonds', first: 250, cursor: cursor || undefined };
    const res  = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ query: GQL, variables: vars }) });
    const json = await res.json();
    const conn = json.data?.collection?.products;
    if (!conn) break;
    all      = all.concat(conn.edges.map(e => norm(e.node)));
    hasMore  = conn.pageInfo.hasNextPage;
    cursor   = conn.pageInfo.endCursor;
  }
  return all;
}

/* ── REST fallback: /collections/{handle}/products.json ─────────────────────
   Reads diamond specs from product tags: shape:Round, color:D, clarity:VS1, etc. */
async function fetchRest(handle, page, acc) {
  page = page || 1; acc = acc || [];
  const res  = await fetch(`/collections/${handle || 'all-diamonds'}/products.json?limit=250&page=${page}`);
  const json = await res.json();
  const products = (json.products || []).map(p => {
    const tagOf = pr => {
      const t = (p.tags || []).find(t => t.toLowerCase().startsWith(pr + ':'));
      return t ? t.slice(pr.length + 1).trim() : '';
    };
    const caratM = p.title.match(/(\d+(?:\.\d+)?)\s*ct/i);
    return {
      id:      p.id,
      handle:  p.handle,
      title:   p.title,
      image:   p.images?.[0]?.src || '',
      price:   parseFloat(p.variants?.[0]?.price || 0),
      currency: 'USD',
      shape:   tagOf('shape')   || '',
      carat:   parseFloat(tagOf('carat') || (caratM ? caratM[1] : '0')) || 0,
      color:   tagOf('color')   || '',
      clarity: tagOf('clarity') || '',
      cut:     tagOf('cut')     || '',
      cert:    tagOf('cert')    || tagOf('lab') || '',
      origin:  tagOf('origin')  || '',
    };
  });
  const combined = acc.concat(products);
  if (products.length === 250) return fetchRest(handle, page + 1, combined);
  return combined;
}

/* ── Filter constants ──────────────────────────────────────────────────────── */
const SHAPES        = ['All','Round','Oval','Cushion','Princess','Emerald','Pear','Radiant','Marquise','Heart','Asscher'];
const COLOR_ORDER   = ['D','E','F','G','H','I','J','K'];
const CLARITY_ORDER = ['FL','IF','VVS1','VVS2','VS1','VS2','SI1','SI2'];
const CUTS          = ['All','Ideal','Excellent','Very Good','Good'];
const CERTS         = ['All','GIA','IGI','HRD'];

const colorIdx   = c => { const i = COLOR_ORDER.indexOf(c);   return i < 0 ? 99 : i; };
const clarityIdx = c => { const i = CLARITY_ORDER.indexOf(c); return i < 0 ? 99 : i; };

const fmtPrice = (p, currency) => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(p); }
  catch { return '$' + Math.round(p).toLocaleString(); }
};

function defaultFilters() {
  return { shape: 'All', caratMin: 0.3, caratMax: 10, colorMax: 7, clarityMax: 7, cut: 'All', cert: 'All', priceMax: 0 };
}

function applyFilters(products, f) {
  const maxP = f.priceMax || 0;
  return products.filter(p => {
    if (f.shape !== 'All' && p.shape !== f.shape) return false;
    if (p.carat < f.caratMin || (f.caratMax < 10 && p.carat > f.caratMax)) return false;
    if (f.colorMax < 7   && colorIdx(p.color) > f.colorMax)       return false;
    if (f.clarityMax < 7 && clarityIdx(p.clarity) > f.clarityMax) return false;
    if (f.cut  !== 'All' && p.cut  !== f.cut)  return false;
    if (f.cert !== 'All' && p.cert !== f.cert) return false;
    if (maxP > 0 && p.price > maxP)            return false;
    return true;
  });
}

/* ── SVG shape glyphs ──────────────────────────────────────────────────────── */
const SHAPE_SVGS = {
  Round:    '<ellipse cx="50" cy="50" rx="36" ry="36" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Oval:     '<ellipse cx="50" cy="50" rx="26" ry="38" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Cushion:  '<rect x="16" y="16" width="68" height="68" rx="18" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Princess: '<rect x="18" y="18" width="64" height="64" rx="2" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Emerald:  '<rect x="18" y="26" width="64" height="48" rx="3" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Pear:     '<path d="M50 14C72 14 82 30 82 46C82 62 68 74 50 86C32 74 18 62 18 46C18 30 28 14 50 14Z" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Radiant:  '<polygon points="32,18 68,18 82,32 82,68 68,82 32,82 18,68 18,32" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Marquise: '<path d="M50 18C78 34 82 66 50 82C18 66 18 34 50 18Z" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Heart:    '<path d="M50 76C20 56 12 36 22 24C32 12 50 22 50 32C50 22 68 12 78 24C88 36 80 56 50 76Z" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  Asscher:  '<polygon points="30,16 70,16 84,30 84,70 70,84 30,84 16,70 16,30" fill="none" stroke="currentColor" stroke-width="2.5"/>',
};

function ShapeGlyph(shape, size) {
  size = size || 32;
  const svg = SHAPE_SVGS[shape] || SHAPE_SVGS.Round;
  return React.createElement('svg', {
    viewBox: '0 0 100 100', width: size, height: size,
    'aria-hidden': 'true', style: { flexShrink: 0, color: 'inherit' },
    dangerouslySetInnerHTML: { __html: svg },
  });
}

/* ── Diamond card ──────────────────────────────────────────────────────────── */
function DiamondCard({ p, showPrices, compareSet, onCompare }) {
  const inCompare = compareSet.has(p.handle);
  return React.createElement('a', { href: '/products/' + p.handle, className: 'ad-card ad-card--diamond' },
    React.createElement('figure', { className: 'ad-card__media' },
      p.image
        ? React.createElement('img', { src: p.image, alt: p.title, loading: 'lazy', width: 400, height: 400 })
        : React.createElement('div', { className: 'ad-card__glyph' }, ShapeGlyph(p.shape || 'Round', 64)),
      p.cert && React.createElement('span', { className: 'ad-mini-badge' }, p.cert),
      p.origin && React.createElement('span', { className: 'ad-mini-badge ad-mini-badge--ink', style:{top:'auto',bottom:10,left:10} }, p.origin)
    ),
    React.createElement('div', { className: 'ad-card__body' },
      React.createElement('h3', { className: 'ad-card__title' },
        p.carat > 0 ? (p.carat.toFixed(2) + ' ct ') : '',
        React.createElement('em', null, p.shape || p.title)
      ),
      React.createElement('p', { className: 'ad-specs' },
        p.color   && React.createElement('span', null, p.color + ' colour'),
        p.clarity && React.createElement('span', null, p.clarity),
        p.cut     && React.createElement('span', null, p.cut + ' cut')
      ),
      React.createElement('div', { className: 'ad-card__foot' },
        showPrices && p.price > 0
          ? React.createElement('span', { className: 'ad-card__price' }, fmtPrice(p.price, p.currency))
          : React.createElement('span', { className: 'ad-card__inquire' }, 'Inquire'),
        React.createElement('button', {
          type: 'button', className: 'ad-card__compare' + (inCompare ? ' is-active' : ''),
          onClick: e => { e.preventDefault(); onCompare(p.handle); },
        }, inCompare ? '✓' : '+')
      )
    )
  );
}

/* ── Range slider component ────────────────────────────────────────────────── */
function RangeSlider({ label, min, max, step, valueMin, valueMax, onMin, onMax, fmt }) {
  fmt = fmt || (v => v);
  return React.createElement('div', { className: 'ad-range' },
    React.createElement('div', { className: 'ad-range__head' },
      React.createElement('span', { className: 'ad-label' }, label),
      React.createElement('span', { className: 'ad-range__val' }, fmt(valueMin) + ' – ' + fmt(valueMax))
    ),
    React.createElement('div', { className: 'ad-range__wrap' },
      React.createElement('input', { type: 'range', min, max, step, value: valueMin,
        className: 'ad-range__input ad-range__input--min',
        onChange: e => onMin(parseFloat(e.target.value)) }),
      React.createElement('input', { type: 'range', min, max, step, value: valueMax,
        className: 'ad-range__input ad-range__input--max',
        onChange: e => onMax(parseFloat(e.target.value)) })
    )
  );
}

/* ── Filters sidebar ───────────────────────────────────────────────────────── */
function Filters({ f, set, maxPrice, total, shown }) {
  return React.createElement('div', { className: 'ad-finder-filters' },

    React.createElement('div', { className: 'ad-finder-filters__head' },
      React.createElement('span', { className: 'ad-eyebrow' }, 'Refine'),
      React.createElement('span', { className: 'ad-finder-count' },
        React.createElement('strong', null, shown.toLocaleString()),
        ' / ' + total.toLocaleString()
      )
    ),

    /* Shape */
    React.createElement('div', { className: 'ad-filter-group' },
      React.createElement('h4', { className: 'ad-filter-group__label' }, 'Shape'),
      React.createElement('div', { className: 'ad-shape-grid' },
        SHAPES.map(s => React.createElement('button', {
          key: s, type: 'button',
          className: 'ad-shape-btn' + (f.shape === s ? ' is-active' : ''),
          onClick: () => set(p => ({ ...p, shape: s })),
        },
          s !== 'All' && ShapeGlyph(s, 20),
          React.createElement('span', null, s === 'All' ? 'All shapes' : s)
        ))
      )
    ),

    /* Carat */
    React.createElement('div', { className: 'ad-filter-group' },
      React.createElement(RangeSlider, {
        label: 'Carat', min: 0.3, max: 10, step: 0.1,
        valueMin: f.caratMin, valueMax: f.caratMax,
        onMin: v => set(p => ({ ...p, caratMin: Math.min(v, p.caratMax - 0.1) })),
        onMax: v => set(p => ({ ...p, caratMax: Math.max(v, p.caratMin + 0.1) })),
        fmt: v => v.toFixed(1) + 'ct',
      })
    ),

    /* Colour */
    React.createElement('div', { className: 'ad-filter-group' },
      React.createElement('div', { className: 'ad-range__head' },
        React.createElement('span', { className: 'ad-label' }, 'Colour'),
        React.createElement('span', { className: 'ad-range__val' },
          f.colorMax >= 7 ? 'D – K' : ('D – ' + COLOR_ORDER[f.colorMax])
        )
      ),
      React.createElement('div', { className: 'ad-color-scale' },
        COLOR_ORDER.map((c, i) => React.createElement('button', {
          key: c, type: 'button',
          className: 'ad-color-btn' + (i <= f.colorMax ? ' is-active' : ''),
          onClick: () => set(p => ({ ...p, colorMax: i === p.colorMax ? 7 : i })),
        }, c))
      )
    ),

    /* Clarity */
    React.createElement('div', { className: 'ad-filter-group' },
      React.createElement('div', { className: 'ad-range__head' },
        React.createElement('span', { className: 'ad-label' }, 'Clarity'),
        React.createElement('span', { className: 'ad-range__val' },
          f.clarityMax >= 7 ? 'FL – SI2' : ('FL – ' + CLARITY_ORDER[f.clarityMax])
        )
      ),
      React.createElement('div', { className: 'ad-clarity-scale' },
        CLARITY_ORDER.map((c, i) => React.createElement('button', {
          key: c, type: 'button',
          className: 'ad-clarity-btn' + (i <= f.clarityMax ? ' is-active' : ''),
          onClick: () => set(p => ({ ...p, clarityMax: i === p.clarityMax ? 7 : i })),
        }, c))
      )
    ),

    /* Cut */
    React.createElement('div', { className: 'ad-filter-group' },
      React.createElement('h4', { className: 'ad-filter-group__label' }, 'Cut'),
      React.createElement('div', { className: 'ad-chip-row' },
        CUTS.map(c => React.createElement('button', {
          key: c, type: 'button',
          className: 'ad-chip' + (f.cut === c ? ' is-active' : ''),
          onClick: () => set(p => ({ ...p, cut: c })),
        }, c))
      )
    ),

    /* Certificate */
    React.createElement('div', { className: 'ad-filter-group' },
      React.createElement('h4', { className: 'ad-filter-group__label' }, 'Certificate'),
      React.createElement('div', { className: 'ad-chip-row' },
        CERTS.map(c => React.createElement('button', {
          key: c, type: 'button',
          className: 'ad-chip' + (f.cert === c ? ' is-active' : ''),
          onClick: () => set(p => ({ ...p, cert: c })),
        }, c))
      )
    ),

    /* Price — only if products have prices */
    maxPrice > 100 && React.createElement('div', { className: 'ad-filter-group' },
      React.createElement(RangeSlider, {
        label: 'Budget', min: 0, max: maxPrice, step: 500,
        valueMin: 0, valueMax: f.priceMax || maxPrice,
        onMin: () => {},
        onMax: v => set(p => ({ ...p, priceMax: v >= maxPrice ? 0 : v })),
        fmt: v => (v >= maxPrice || v === 0) ? 'Any' : fmtPrice(v, 'USD'),
      })
    ),

    /* Reset */
    React.createElement('button', {
      type: 'button', className: 'ad-btn ad-btn--ghost', style: { marginTop: '1rem' },
      onClick: () => set(defaultFilters),
    }, 'Clear all filters')
  );
}

/* ── Main Finder ───────────────────────────────────────────────────────────── */
function Finder({ products, showPrices, loading, error }) {
  const [f, setF]             = React.useState(defaultFilters);
  const [page, setPage]       = React.useState(1);
  const [compare, setCompare] = React.useState(new Set());
  const [sidebar, setSidebar] = React.useState(false); /* mobile filter drawer */
  const PER = 24;

  const maxPrice = React.useMemo(() => {
    const prices = products.filter(p => p.price > 0).map(p => p.price);
    return prices.length ? Math.ceil(Math.max(...prices) / 1000) * 1000 : 0;
  }, [products]);

  const filtered = React.useMemo(() => applyFilters(products, f), [products, f]);
  const paged    = filtered.slice(0, page * PER);
  const hasMore  = paged.length < filtered.length;

  React.useEffect(() => setPage(1), [f]);

  const toggleCompare = handle => {
    setCompare(prev => {
      const n = new Set(prev);
      if (n.has(handle)) n.delete(handle);
      else if (n.size < 4) n.add(handle);
      return n;
    });
  };

  return React.createElement('div', { className: 'ad-finder-root' },

    /* Top bar — mobile toggle + active filters */
    React.createElement('div', { className: 'ad-finder-topbar' },
      React.createElement('div', { className: 'ad-container' },
        React.createElement('div', { className: 'ad-finder-topbar__inner' },
          React.createElement('button', {
            type: 'button', className: 'ad-finder-topbar__toggle',
            onClick: () => setSidebar(o => !o),
          }, sidebar ? '✕ Close filters' : '⊟ Refine'),
          React.createElement('span', { className: 'ad-finder-topbar__count' },
            loading ? 'Loading the suite…' : filtered.length.toLocaleString() + ' stones'
          )
        )
      )
    ),

    /* Main body: sidebar + grid */
    React.createElement('div', { className: 'ad-container' },
      React.createElement('div', { className: 'ad-finder-layout' + (sidebar ? ' sidebar-open' : '') },

        React.createElement('aside', { className: 'ad-finder-aside' },
          React.createElement(Filters, { f, set: setF, maxPrice, total: products.length, shown: filtered.length })
        ),

        React.createElement('div', { className: 'ad-finder-main' },
          loading && React.createElement('div', { className: 'ad-finder-skeleton' },
            [0,1,2,3,4,5,6,7].map(i => React.createElement('div', { key: i, className: 'ad-card ad-card--skeleton' }))
          ),

          !loading && error && React.createElement('p', { className: 'ad-suite-empty' }, error),

          !loading && !error && paged.length === 0 && React.createElement('div', { className: 'ad-suite-empty' },
            React.createElement('p', null, 'No stones match your criteria.'),
            React.createElement('p', { className: 'ad-body', style: { marginTop: '.5rem' } }, 'Our concierge can source a stone to your exact specification.'),
            React.createElement('a', { href: '/pages/consult', className: 'ad-btn ad-btn--gold', style: { marginTop: '1.5rem' } }, 'Speak with a concierge')
          ),

          !loading && !error && paged.length > 0 && React.createElement(React.Fragment, null,
            React.createElement('div', { className: 'ad-suite-grid' },
              paged.map(p => React.createElement(DiamondCard, { key: p.id, p, showPrices, compareSet: compare, onCompare: toggleCompare }))
            ),
            hasMore && React.createElement('div', { className: 'ad-finder-more' },
              React.createElement('button', {
                type: 'button', className: 'ad-btn ad-btn--outline',
                onClick: () => setPage(n => n + 1),
              }, 'Show more · ' + (filtered.length - paged.length).toLocaleString() + ' remaining')
            )
          )
        )
      )
    ),

    /* Compare bar */
    compare.size > 0 && React.createElement('div', { className: 'ad-compare-bar' },
      React.createElement('div', { className: 'ad-container' },
        React.createElement('div', { className: 'ad-compare-bar__inner' },
          React.createElement('span', { className: 'ad-label', style: { color: 'var(--ad-gold)' } },
            'Comparing ' + compare.size + (compare.size < 4 ? '/4' : ' stones')
          ),
          React.createElement('div', { className: 'ad-compare-bar__chips' },
            Array.from(compare).map(h => {
              const p = products.find(x => x.handle === h);
              return React.createElement('span', { key: h, className: 'ad-compare-chip' },
                p ? (p.carat > 0 ? p.carat.toFixed(2) + 'ct ' : '') + (p.shape || h) : h,
                React.createElement('button', { type: 'button', className: 'ad-compare-chip__x', onClick: () => toggleCompare(h) }, '×')
              );
            })
          ),
          compare.size >= 2 && React.createElement('a', {
            href: '/pages/compare?stones=' + Array.from(compare).join(','),
            className: 'ad-btn ad-btn--gold',
          }, 'Compare')
        )
      )
    )
  );
}

/* ── React loader ──────────────────────────────────────────────────────────── */
let _rp;
function loadReact() {
  if (_rp) return _rp;
  _rp = new Promise((resolve, reject) => {
    const a = document.createElement('script');
    a.src = (window.__themeAssets && window.__themeAssets.react) || '/assets/react.production.min.js';
    a.onload = () => {
      const b = document.createElement('script');
      b.src = (window.__themeAssets && window.__themeAssets.reactDom) || '/assets/react-dom.production.min.js';
      b.onload = resolve; b.onerror = reject;
      document.head.appendChild(b);
    };
    a.onerror = reject;
    document.head.appendChild(a);
  });
  return _rp;
}

/* ── Custom element ────────────────────────────────────────────────────────── */
class AtelierFinder extends HTMLElement {
  async connectedCallback() {
    if (this.hydrated) return;
    this.hydrated = true;
    const collection = this.dataset.collection || 'all-diamonds';
    const showPrices = this.dataset.showPrices === 'true';

    await loadReact();
    const root = ReactDOM.createRoot(this);
    root.render(React.createElement(Finder, { products: [], showPrices, loading: true, error: null }));

    let products = [], error = null;
    try {
      const cfg = window.adTheme || {};
      if (cfg.storefrontToken && cfg.shop) {
        products = await fetchStorefront(cfg.shop, cfg.storefrontToken, collection);
      } else {
        products = await fetchRest(collection);
      }
    } catch (e) {
      error = 'Could not load the suite — please refresh or contact us.';
      console.error('[AtelierFinder]', e);
    }

    root.render(React.createElement(Finder, { products, showPrices, loading: false, error }));
  }
}

customElements.define('atelier-finder', AtelierFinder);
